import { useEffect, useState } from 'react';

import { Modal } from '../ui';
import { api } from '../../lib/api';
import { ApplyDialog } from './ApplyDialog';
import { BaselineMatrix } from './BaselineMatrix';
import { GroupDialog } from './GroupDialog';
import { RuleDialog } from './RuleDialog';
import { RulesTable } from './RulesTable';
import type { Capabilities, FirewallOverview, GroupView, PendingApply, RuleView, SystemView } from './types';
import { errorText } from './types';

function seconds(iso: string, now: number): number {
  return Math.max(0, Math.round((Date.parse(iso) - now) / 1000));
}

export function FirewallSection({
  overview,
  caps,
  onChanged,
}: {
  overview: FirewallOverview;
  caps: Capabilities;
  onChanged: () => void;
}) {
  const [ruleDialog, setRuleDialog] = useState<{ open: boolean; rule: RuleView | null }>({ open: false, rule: null });
  const [groupDialog, setGroupDialog] = useState<{ open: boolean; group: GroupView | null }>({ open: false, group: null });
  const [applyOpen, setApplyOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ kind: 'rule' | 'group'; id: string; label: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const pending: PendingApply | null = overview.pendingApply;
  const pendingLive = pending?.status === 'PENDING';

  useEffect(() => {
    if (!pendingLive) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pendingLive]);

  const toggleRule = async (r: RuleView) => {
    setError(null);
    try {
      await api(`/connectiq/firewall/rules/${r.id}`, { method: 'PATCH', body: JSON.stringify({ enabled: !r.enabled }) });
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setError(null);
    try {
      await api(
        confirmDelete.kind === 'rule' ? `/connectiq/firewall/rules/${confirmDelete.id}` : `/connectiq/firewall/groups/${confirmDelete.id}`,
        { method: 'DELETE' },
      );
      setConfirmDelete(null);
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const confirmOrRoll = async (kind: 'confirm' | 'rollback') => {
    if (!pending) return;
    setError(null);
    try {
      await api(`/connectiq/firewall/apply/${pending.applyId}/${kind}`, { method: 'POST' });
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const hostTone = overview.host.hasReading ? (overview.host.inSync ? 'ok' : 'warn') : 'unknown';
  const hostLabel = !overview.host.hasReading
    ? 'Host not read yet'
    : overview.host.inSync
      ? 'Host matches the rules'
      : `Host differs: ${overview.host.missing.length} missing, ${overview.host.extra.length} unexpected`;

  return (
    <section className="ciq-fw" aria-label="Firewall">
      <header className="ciq-fw-head">
        <div>
          <h2>Firewall</h2>
          <p className="ciq-muted">
            Managed network <span className="mono">{overview.subnet}</span>. Other stacks on the host are not affected.
          </p>
        </div>
        <div className="ciq-actions">
          <span className={`ciq-status ${hostTone}`}>
            <span aria-hidden="true">{hostTone === 'ok' ? '●' : hostTone === 'warn' ? '▲' : '○'}</span> {hostLabel}
          </span>
          {caps.canManageFirewall && (
            <button type="button" className="ghost" onClick={() => setGroupDialog({ open: true, group: null })}>
              New group
            </button>
          )}
          {caps.canTouchProduction && (
            <button type="button" className="ghost" onClick={() => setApplyOpen(true)} disabled={pendingLive}>
              Apply to host…
            </button>
          )}
        </div>
      </header>

      {pendingLive && pending && (
        <div className="ciq-banner" role="status">
          <div>
            <strong>Change {pending.applyId} is live and waiting for confirmation.</strong>
            <div className="ciq-muted small">The host restores the previous rules in {seconds(pending.expiresAt, now)}s unless you confirm.</div>
          </div>
          {caps.canTouchProduction && (
            <div className="ciq-actions">
              <button type="button" onClick={() => confirmOrRoll('confirm')}>Keep change</button>
              <button type="button" className="ghost" onClick={() => confirmOrRoll('rollback')}>Roll back now</button>
            </div>
          )}
        </div>
      )}

      {overview.warnings.length > 0 && (
        <ul className="ciq-warnings">
          {overview.warnings.map((w) => (
            <li key={w}>▲ {w}</li>
          ))}
        </ul>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}

      <RulesTable
        overview={overview}
        canManage={caps.canManageFirewall}
        onAdd={() => setRuleDialog({ open: true, rule: null })}
        onEdit={(rule) => setRuleDialog({ open: true, rule })}
        onToggle={toggleRule}
        onDelete={(rule) => setConfirmDelete({ kind: 'rule', id: rule.id, label: `${rule.action === 'ALLOW' ? 'Allow' : 'Block'} ${rule.portLabel}` })}
      />

      <BaselineMatrix overview={overview} canManage={caps.canManageFirewall} onChanged={onChanged} />

      <div className="ciq-subhead">Systems and groups</div>
      <div className="ciq-cards">
        {overview.systems.map((s: SystemView) => (
          <article key={s.id} className="ciq-card">
            <div className="ciq-card-title">{s.name}</div>
            <div className="mono ciq-muted small">{s.address ?? 'no container address'}</div>
            <div className="ciq-muted small">{s.hostname}</div>
            {s.role && <span className="ciq-chip">{s.role === 'jump-host' ? 'Jump host' : s.role === 'cert-authority' ? 'SSH CA' : s.role}</span>}
          </article>
        ))}
        {overview.groups.map((g) => (
          <article key={g.id} className="ciq-card ciq-card-group">
            <div className="ciq-card-title">{g.name}</div>
            {g.description && <div className="ciq-muted small">{g.description}</div>}
            <div className="ciq-chips">
              {g.memberIds.map((id) => (
                <span key={id} className="ciq-chip">{overview.systems.find((s) => s.id === id)?.name ?? 'Unknown'}</span>
              ))}
            </div>
            <div className="ciq-card-foot">
              <span className="ciq-muted small">{g.ruleCount} rule{g.ruleCount === 1 ? '' : 's'}</span>
              {caps.canManageFirewall && (
                <span className="ciq-actions">
                  <button type="button" className="ghost small-btn" onClick={() => setGroupDialog({ open: true, group: g })}>Edit</button>
                  <button type="button" className="ghost small-btn" onClick={() => setConfirmDelete({ kind: 'group', id: g.id, label: g.name })}>Delete</button>
                </span>
              )}
            </div>
          </article>
        ))}
      </div>

      <RuleDialog
        open={ruleDialog.open}
        editing={ruleDialog.rule}
        onClose={() => setRuleDialog({ open: false, rule: null })}
        onSaved={onChanged}
        systems={overview.systems}
        groups={overview.groups}
      />
      <GroupDialog
        open={groupDialog.open}
        editing={groupDialog.group}
        onClose={() => setGroupDialog({ open: false, group: null })}
        onSaved={onChanged}
        systems={overview.systems}
      />
      <ApplyDialog open={applyOpen} onClose={() => setApplyOpen(false)} hostname={overview.host.hostname} onApplied={onChanged} />

      <Modal open={confirmDelete !== null} onClose={() => setConfirmDelete(null)} title={`Delete ${confirmDelete?.kind === 'group' ? 'group' : 'rule'}?`}>
        <p>
          <strong>{confirmDelete?.label}</strong> will be removed. The change reaches the host only when you apply it.
        </p>
        <div className="ciq-actions">
          <button type="button" className="ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button type="button" className="ciq-danger" onClick={doDelete}>Delete</button>
        </div>
      </Modal>
    </section>
  );
}
